// U15 — Ui::ExpressionInputComponent. A single-line input with an autocomplete
// listbox for expression-like text. The controller knows nothing about what
// the text means: the HOST supplies suggestions, either from `url` (GET
// ?q=<token>&cursor=<caret> → JSON Array or {suggestions: [...]}, items
// {value, label?, description?}) or from a JS provider — a cancelable
// `ui--expression-input:query` event whose listener calls preventDefault()
// and answers with detail.respond(items).
//
// The token being completed is the run of characters before the caret that
// matches `tokenPattern` (a JS RegExp source, `$`-anchored for you; default:
// identifier / path characters). Accepting a suggestion replaces the token,
// puts the caret after it, then fires `input` and `ui--expression-input:select`.
//
// Stale answers are dropped (a request counter guards both the fetch and the
// respond() path) and an in-flight fetch is aborted by the next query. All
// suggestion text is written with textContent — never innerHTML.
import { Controller } from "@hotwired/stimulus"

const DEFAULT_TOKEN = /[\w$.@#:-]*$/

export default class extends Controller {
  static targets = ["input", "list", "optionTemplate", "status"]

  static values = {
    url: String,
    minChars: { type: Number, default: 0 },
    debounce: { type: Number, default: 150 },
    tokenPattern: String,
    countTemplate: { type: String, default: "%{count}" },
    emptyText: String
  }

  connect() {
    this._requestId = 0
    this._activeIndex = -1
    this._staleSince = false
    this._items = []
    this._token = null
    this._timer = null
    this._abort = null
    this._accepting = false
    // Keep focus in the input while the pointer picks an option: mousedown's
    // default would blur it (and close the list) before click lands.
    this._onListMouseDown = (event) => {
      const option = event.target.closest("[role='option']")
      if (!option || !this.listTarget.contains(option)) return
      event.preventDefault()
      this._accept(Number(option.dataset.index))
    }
    this.listTarget.addEventListener("mousedown", this._onListMouseDown)
  }

  disconnect() {
    clearTimeout(this._timer)
    this._abort?.abort()
    this.listTarget.removeEventListener("mousedown", this._onListMouseDown)
  }

  // input->ui--expression-input#input
  input(event) {
    if (this._accepting || event.isComposing) return
    // The open suggestions were fetched for the PREVIOUS text: until the
    // debounced re-query answers, Enter/Tab must not accept one of them.
    this._staleSince = true
    this._deactivate()
    clearTimeout(this._timer)
    this._timer = setTimeout(() => this._query(), this.debounceValue)
  }

  // keydown->ui--expression-input#keydown
  keydown(event) {
    if (event.isComposing) return
    const open = !this.listTarget.hidden

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (!open || this._staleSince) return this._query()
        this._move(1)
        break
      case "ArrowUp":
        if (!open) return
        event.preventDefault()
        this._move(-1)
        break
      case "Enter":
      case "Tab":
        if (!open || this._activeIndex < 0 || this._staleSince) return
        event.preventDefault()
        this._accept(this._activeIndex)
        break
      case "Escape":
        if (!open) return
        event.preventDefault()
        event.stopPropagation()
        this._close()
        break
    }
  }

  // blur->ui--expression-input#blur
  blur() {
    clearTimeout(this._timer)
    this._close()
  }

  // --- querying ---

  _query() {
    const field = this.inputTarget
    const text = field.value
    const cursor = field.selectionStart ?? text.length
    const token = this._tokenBefore(text, cursor)

    if (token.text.length < this.minCharsValue) return this._close()

    clearTimeout(this._timer)
    this._staleSince = false
    const id = ++this._requestId
    this._abort?.abort()
    this._abort = null
    this._token = token

    const respond = (items) => {
      if (id !== this._requestId) return // superseded by a newer query
      this._show(items)
    }
    const query = new CustomEvent("ui--expression-input:query", {
      bubbles: true,
      cancelable: true,
      detail: { query: token.text, cursor, text, respond }
    })
    // A host listener that owns the query calls preventDefault() and respond().
    this.element.dispatchEvent(query)
    if (query.defaultPrevented) return
    if (!this.urlValue) return this._close()

    this._fetch(id, token.text, cursor, respond)
  }

  async _fetch(id, q, cursor, respond) {
    const url = new URL(this.urlValue, window.location.href)
    url.searchParams.set("q", q)
    url.searchParams.set("cursor", String(cursor))
    const controller = new AbortController()
    this._abort = controller

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: controller.signal
      })
      if (!response.ok) throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status })
      respond(await response.json())
    } catch (error) {
      if (error?.name === "AbortError" || id !== this._requestId) return
      this._close()
      this.element.dispatchEvent(new CustomEvent("ui--expression-input:error", {
        bubbles: true,
        detail: { error, status: error?.status ?? null }
      }))
    }
  }

  _tokenBefore(text, cursor) {
    const before = text.slice(0, cursor)
    const match = before.match(this._tokenRegExp())
    const tokenText = match ? match[0] : ""
    return { text: tokenText, start: cursor - tokenText.length, end: cursor }
  }

  _tokenRegExp() {
    if (!this.tokenPatternValue) return DEFAULT_TOKEN
    try {
      const source = this.tokenPatternValue
      return new RegExp(source.endsWith("$") ? source : `${source}$`)
    } catch {
      return DEFAULT_TOKEN // a bad host pattern degrades to the default, never throws
    }
  }

  // --- rendering ---

  _show(payload) {
    const items = this._normalize(payload)
    this._items = items
    this._activeIndex = -1
    this._staleSince = false
    this.listTarget.replaceChildren()

    if (!items.length) {
      this._close()
      this.statusTarget.textContent = this.emptyTextValue
      return
    }

    items.forEach((item, index) => {
      const option = this.optionTemplateTarget.content.firstElementChild.cloneNode(true)
      option.id = `${this.inputTarget.id}-opt-${index}`
      option.dataset.index = String(index)
      option.setAttribute("aria-selected", "false")
      option.querySelector("[data-part='label']").textContent = item.label
      const description = option.querySelector("[data-part='description']")
      if (item.description) description.textContent = item.description
      else description.remove()
      this.listTarget.append(option)
    })

    this.listTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.statusTarget.textContent = this.countTemplateValue.replace("%{count}", String(items.length))
  }

  _normalize(payload) {
    const list = Array.isArray(payload) ? payload : payload?.suggestions
    if (!Array.isArray(list)) return []
    return list.flatMap((entry) => {
      const item = typeof entry === "string" ? { value: entry } : entry
      if (!item || typeof item !== "object" || item.value == null || item.value === "") return []
      const value = String(item.value)
      return [{
        value,
        label: item.label == null ? value : String(item.label),
        description: item.description == null ? "" : String(item.description)
      }]
    })
  }

  _deactivate() {
    this._activeIndex = -1
    this.inputTarget.removeAttribute("aria-activedescendant")
    Array.from(this.listTarget.children).forEach((option) => option.setAttribute("aria-selected", "false"))
  }

  _move(step) {
    const count = this._items.length
    if (!count) return
    this._activeIndex = (this._activeIndex + step + count) % count
    Array.from(this.listTarget.children).forEach((option, index) => {
      const active = index === this._activeIndex
      option.setAttribute("aria-selected", String(active))
      if (active) {
        option.scrollIntoView?.({ block: "nearest" })
        this.inputTarget.setAttribute("aria-activedescendant", option.id)
      }
    })
  }

  _accept(index) {
    const item = this._items[index]
    if (!item) return

    // Re-read the token from the CURRENT text and caret: `this._token` was
    // captured when the query ran and is stale once the user has typed since
    // (a click can land inside the debounce window), which would splice the
    // suggestion into the wrong range.
    clearTimeout(this._timer)
    const field = this.inputTarget
    const text = field.value
    const token = this._tokenBefore(text, field.selectionStart ?? text.length)
    field.value = text.slice(0, token.start) + item.value + text.slice(token.end)
    const caret = token.start + item.value.length
    field.setSelectionRange(caret, caret)

    this._accepting = true
    try {
      field.dispatchEvent(new Event("input", { bubbles: true }))
    } finally {
      this._accepting = false
    }
    this._close()
    this.element.dispatchEvent(new CustomEvent("ui--expression-input:select", {
      bubbles: true,
      detail: { item, token: token.text, value: field.value }
    }))
  }

  _close() {
    this._requestId++ // late answers for the query we just abandoned are stale
    this._abort?.abort()
    this._abort = null
    this._items = []
    this._activeIndex = -1
    this.listTarget.hidden = true
    this.listTarget.replaceChildren()
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }
}
